import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Image, StyleSheet, Text, View } from 'react-native';

import StoreChipPng from '../../../assets/desktop/dock/store.png';
import { JackpotTier, useJackpotCycle } from '../../../utils/hooks/useJackpotCycle';
import { useJackpotAmounts } from '../../shell/header/TopJackpotBanner';
import { Pressable } from '../common/Touchable';
import JackpotHistoryModal from '../Modals/JackpotHistoryModal';

// Figma "Jackpot Bar" (1625:4585): 596x82, a #210503 plate on a 1.5 #B25200 hairline with a 20
// radius and an orange bloom, JACKPOT / chip / amount 24 apart. Both labels are Sofia Sans Bold 36
// in the gold text gradient. Numbers are in the desktop design space.
//
// The chip is pinned to the bar's centre, JACKPOT ending 24 left of it and the amount starting 24
// right of it: centring the three as one group pushed the chip off-centre whenever the amount was
// shorter or longer than JACKPOT.
export const JACKPOT_BAR = { width: 596, height: 82 } as const;
const LABEL = { width: 143, height: 44, fontSize: 36 } as const;
const CHIP = { width: 61, height: 63 } as const;
/**
 * The chip art (dock/store.png, 149x152) carries a soft shadow under the coin: the coin itself
 * spans y 1-124, so its centre sits 13.5 of the 152 above the image's. Centring the image left
 * the coin visibly high in the bar; this moves it back down onto the bar's centre line.
 */
const CHIP_ART_DROP = (CHIP.height * 13.5) / 152;
const GAP = 24;

type Props = {
	width: number;
	/** Design-to-pixel factor. */
	scale: number;
};

export const DesktopJackpotBar = ({ width, scale }: Props) => {
	const { t } = useTranslation();
	const amounts = useJackpotAmounts();
	const { current, next, offset } = useJackpotCycle(amounts);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const s = (v: number) => v * scale;
	const styles = createStyles(s, width);
	const renderTierText = (tier: JackpotTier) => {
		const label = `${t('T_Jackpot', { defaultValue: 'JACKPOT' })} ${tier.number}`;
		const amountFontSize = s(
			tier.amount.length > 11 ? 30 : tier.amount.length > 9 ? 33 : LABEL.fontSize,
		);

		return (
			<View style={styles.tierRow}>
				<View style={[styles.side, styles.sideStart]}>
					<Text
						numberOfLines={1}
						adjustsFontSizeToFit
						style={[styles.tierText, { color: tier.color }]}
					>
						{label}
					</Text>
				</View>
				<View style={styles.chipSpace} />
				<View style={[styles.side, styles.sideEnd]}>
					<Text
						numberOfLines={1}
						adjustsFontSizeToFit
						style={[
							styles.tierText,
							styles.amountText,
							{ color: tier.color, fontSize: amountFontSize },
						]}
					>
						{tier.amount}
					</Text>
				</View>
			</View>
		);
	};

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_JACKPOT_HISTORY', { defaultValue: 'Jackpot history' })}
			onPress={() => setIsHistoryOpen(true)}
			style={styles.bar}
		>
			<View style={styles.tierViewport}>
				<Animated.View
					style={[
						styles.tierStack,
						{
							transform: [
								{
									translateY: offset.interpolate({
										inputRange: [0, 1],
										outputRange: [0, -s(JACKPOT_BAR.height)],
									}),
								},
							],
						},
					]}
				>
					{renderTierText(current)}
					{renderTierText(next)}
				</Animated.View>
			</View>
			<Image source={StoreChipPng} style={styles.chip} resizeMode='contain' />
			{isHistoryOpen ? <JackpotHistoryModal onClose={() => setIsHistoryOpen(false)} /> : null}
		</Pressable>
	);
};

const createStyles = (s: (v: number) => number, width: number) =>
	StyleSheet.create({
		bar: {
			width,
			height: s(JACKPOT_BAR.height),
			position: 'relative',
			borderRadius: s(20),
			borderWidth: s(1.5),
			borderColor: '#B25200',
			backgroundColor: '#210503',
			boxShadow: `0 0 ${s(16.2)}px rgba(255,66,0,0.7), 0 0 ${s(48.6)}px rgba(255,66,0,0.5), inset 0 0 ${s(16.2)}px rgba(255,96,0,0.4)`,
		} as any,
		chip: {
			position: 'absolute',
			left: '50%',
			top: '50%',
			width: s(CHIP.width),
			height: s(CHIP.height),
			transform: [
				{ translateX: -s(CHIP.width) / 2 },
				{ translateY: -s(CHIP.height) / 2 + s(CHIP_ART_DROP) },
			],
		},
		tierViewport: { height: s(JACKPOT_BAR.height), overflow: 'hidden' },
		tierStack: { height: s(JACKPOT_BAR.height * 2) },
		tierRow: {
			height: s(JACKPOT_BAR.height),
			flexDirection: 'row',
			alignItems: 'center',
			paddingHorizontal: s(24),
		},
		// Equal sides and a fixed centre spacer keep the chip on the bar's centre line.
		side: { flex: 1, minWidth: 0 },
		sideStart: { justifyContent: 'flex-end' },
		sideEnd: { justifyContent: 'flex-end' },
		chipSpace: { width: s(CHIP.width + GAP * 2) },
		tierText: {
			fontFamily: 'Sofia Sans',
			fontSize: s(27),
			fontWeight: '700',
			letterSpacing: s(0.4),
			textShadowColor: '#421000',
			textShadowOffset: { width: s(1), height: s(1) },
			textShadowRadius: s(1),
		},
		amountText: { textAlign: 'right' },
	});

export default DesktopJackpotBar;
